import { type ReactNode, useImperativeHandle, useRef, forwardRef } from "react";
import {
  useForm,
  type SubmitHandler,
  type UseFormReturn,
  type UseFormProps,
} from "react-hook-form";

import { useAPI } from "/src/hooks/useAPI";

const { callAPI } = useAPI();

type FormProps<T extends Record<string, any>> = {
  action: string;
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  onBefore?: () => void;
  children: (form: UseFormReturn<T>) => ReactNode;
  onSuccess?: (data: unknown) => void;
  onError?: (error: unknown) => void;
  onValues?: (data: unknown) => void;
  auth?: boolean;
  options?: UseFormProps<T>;
  transformValues?: (data: T) => Record<string, any>;
};

export type FormHandle = {
  submit: () => void;
};

const Form = forwardRef<FormHandle, FormProps<any>>(
  (
    {
      action,
      method = "POST",
      onBefore,
      children,
      onSuccess,
      onValues,
      onError,
      auth = false,
      options = {},
      transformValues
    },
    ref,
  ) => {
    const form = useForm(options);
    const { callAPI } = useAPI();
    // Sync guard against rapid double-submits — React state lags a click,
    // and RHF's handleSubmit yields before calling us, so we have to gate
    // at the DOM event handler with a ref.
    const submittingRef = useRef(false);

    const handleSubmit: SubmitHandler<any> = async (values) => {
      if (onBefore) onBefore();

      try {
        // 1. Initialize headers object
        const headers: Record<string, string> = {};

        // 2. Add Content-Type for non-GET requests
        if (method !== "GET") {
          headers["Content-Type"] = "application/json";
        }

        // 3. Authentication rides on the HttpOnly session cookie (callAPI sends
        // credentials: "include"); no JS-readable token is involved. The `auth`
        // flag is retained for API compatibility with existing callers.
        void auth;

        onValues?.(values)

        const payload = transformValues ? transformValues(values) : values;

        if (action !== "#") {
          let res;
          try {
            res = await callAPI(action, {
              method,
              headers, // Pass the constructed headers
              body: method === "GET" ? undefined : payload,
            });
          } catch (err) {
            throw new Error(err);
          }

          onSuccess?.(res);
        }

      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : typeof err === "string"
              ? err
              : (err?.error ?? "An unexpected error has occured");

        onError?.(errorMessage);
      }
    };

    const gatedSubmit = (e?: React.FormEvent) => {
      if (submittingRef.current) {
        e?.preventDefault();
        return;
      }
      submittingRef.current = true;
      return form
        .handleSubmit(handleSubmit)(e as React.BaseSyntheticEvent)
        .finally(() => {
          submittingRef.current = false;
        });
    };

    // Expose the submit method via the ref
    useImperativeHandle(ref, () => ({
      submit: () => gatedSubmit(),
    }));

    return (
      <form onSubmit={gatedSubmit}>
        <div className="fields-container">{children(form)}</div>
      </form>
    );
  },
);

Form.displayName = "Form";

export default Form;